import { z } from "zod";
import { useFunnel } from "@airhelp/funnel";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import { useTranslation } from "react-i18next";
import { useForm, useFormHandlers } from "../../../modules/form";
import {
  FunnelStepBackControl,
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import {
  getArrivalAirport,
  getDepartureAirport,
  groupItineraryFlights,
  updateFlightDetails,
  updateItineraryFlights,
  usePreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import { ConnectionsControl } from "v2/modules/form/ConnectionsControl";
import { airportSchema } from "./FlightDetails";
import { Airport, ItineraryFlights } from "v2/modules/types/intake-funnel";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";

const stepSchema = z.object({
  connections: z.array(z.object({ airport: airportSchema })),
});
const defaultStepSchema = z.object({
  connections: z.array(z.object({ airport: airportSchema.nullable() })),
});

export function ConnectionsStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { flightDetails, itineraryFlights } = usePreEligibilityDataSnapshot();

  const departureAirport = getDepartureAirport();
  const arrivalAirport = getArrivalAirport();

  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      connections: flightDetails.connections as { airport: Airport | null }[],
    },
  });

  const { onSubmit, showError } = useFormHandlers({
    schema: defaultStepSchema,
    onSubmit({ connections }) {
      if (connections) {
        const validConnections = connections.filter((item) => item.airport !== null) as {
          airport: Airport;
        }[];

        if (
          !departureAirport ||
          !arrivalAirport ||
          !(validConnections.length === connections.length)
        ) {
          throw new Error("Invalid connections data");
        }

        updateFlightDetails({ connections });

        const groupedItineraryFlights = groupItineraryFlights({
          departureAirport,
          arrivalAirport,
          connections: validConnections,
        });

        const actualItineraryFlightsLength = itineraryFlights.length;
        const newItineraryFlightsLength = groupedItineraryFlights.length;

        if (
          actualItineraryFlightsLength > 1 &&
          newItineraryFlightsLength === actualItineraryFlightsLength
        ) {
          const areTheSameFlights = compareItineraryFlights(
            itineraryFlights,
            groupedItineraryFlights,
          );

          if (!areTheSameFlights) {
            updateItineraryFlights(groupedItineraryFlights);
          }
        } else if (
          actualItineraryFlightsLength !== newItineraryFlightsLength ||
          actualItineraryFlightsLength === 1
        ) {
          updateItineraryFlights(groupedItineraryFlights);
        }
      }

      trackClaimProcess({ funnelStep: funnel.currentStep?.name });
      trackEnquiryStepComplete();
      funnel.stepForward();
    },
  });

  return (
    <FunnelStepContainer data-testid="connectionsStep">
      <FunnelStepSection title={t("steps.connections.text")}>
        <ConnectionsControl control={control} name="connections" />
      </FunnelStepSection>

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        <FunnelStepBackControl />
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}

/**
 * @param itineraryFlights
 * @param newItineraryFlights
 * @returns Boolean
 * This function compares the passed itineraryFlights and returns a boolean value
 * based on the comparison. It checks if the departure and arrival airports are equal in both
 * itineraryFlights. If they are equal, it returns true, otherwise it returns false.
 */
export function compareItineraryFlights(
  itineraryFlights: Readonly<ItineraryFlights>,
  newItineraryFlights: Readonly<ItineraryFlights>,
) {
  return itineraryFlights.every((itineraryFlight, index) => {
    const departureAirportsAreEqual =
      itineraryFlight.departureAirport?.iata === newItineraryFlights[index].departureAirport?.iata;

    const arrivalAirportsAreEqual =
      itineraryFlight.arrivalAirport?.iata === newItineraryFlights[index].arrivalAirport?.iata;

    return departureAirportsAreEqual && arrivalAirportsAreEqual;
  });
}
